import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { getTeamMemberRatesOnProject } from '../api/team-member-rates.api';
import { GetTeamMemberRatesOnProjectRequest } from '../types/dto/request/get-team-member-rates-on-project-request';
import { TeamMemberRatesOnProjectResponse } from '../types/dto/response/team-member-rates-on-project-response';

interface UseTeamMemberRatesOnProjectParams {
  /** Team member ID */
  teamMemberId: number;
  /** Project ID */
  projectId: number;
  /** Effective date for rate calculation (ISO 8601 date-time) */
  effectiveDate: string;
  /** Whether the query should be enabled */
  enabled?: boolean;
  /** Additional React Query options */
  options?: Omit<
    UseQueryOptions<TeamMemberRatesOnProjectResponse, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

/**
 * Hook to fetch team member rates on a specific project.
 * Returns all available rate options for the user and the project's rate configuration.
 *
 * @param params - Query parameters
 * @returns React Query result with team member rates information
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useTeamMemberRatesOnProject({
 *   teamMemberId: 123,
 *   projectId: 456,
 *   effectiveDate: '2025-01-01T00:00:00Z',
 * });
 *
 * if (data) {
 *   console.log('Available rate options:', data.rateOptions);
 *   console.log('Project rate source:', data.projectBasedRateOption.source);
 * }
 * ```
 */
export const useTeamMemberRatesOnProject = ({
  teamMemberId,
  projectId,
  effectiveDate,
  enabled = true,
  options,
}: UseTeamMemberRatesOnProjectParams) => {
  const queryParams: GetTeamMemberRatesOnProjectRequest = {
    teamMemberId,
    projectId,
    effectiveDate,
  };

  return useQuery<TeamMemberRatesOnProjectResponse, Error>({
    queryKey: ['team-member-rates-on-project', queryParams],
    queryFn: () => getTeamMemberRatesOnProject(queryParams),
    enabled: enabled && Boolean(teamMemberId) && Boolean(projectId) && Boolean(effectiveDate),
    ...options,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });
};
